/*
 * The routes on the board, drawn as light by an original WebGL shader written for PAWNED (see
 * CREDITS.md): every pixel near a route knows how far it is from the route, how far along the route
 * it is, and on which side, and is painted from that.
 *
 * - The trail of the latest turn: a ribbon in the color of the player who made it, fainter where
 *   the turn began, with a ring where the pawn set out and a dot on each landing.
 * - A move being previewed: a beam of light in a glow that seethes, with arrows of light running
 *   along it toward the end, a ring on each landing and a target that pulses on the last one.
 * - An Epitaph's replay: a ghostly stream of smoke, with motes drifting along it.
 *
 * Both are drawn on one canvas under the pieces, so the pieces stand in the light; a landing on a
 * pawn rings it. A new route is drawn in, from its start (or from where the route before it ended,
 * when it only grew). The canvas has the board's own resolution (up to two pixels per CSS pixel).
 * It moves at most 30 frames a second, only while a move or an Epitaph is shown or a route is drawn
 * in, and not at all while the page is hidden, when motion is reduced, when the graphics are drawn
 * by the processor, or when the device cannot keep up (src/ui/frameWatch.ts): then each route is a
 * still picture. Without WebGL the board draws its routes with SVG instead (BoardRoutes.tsx).
 */
import { FrameWatch } from './frameWatch';
import { compile, softwareRendered } from './webgl';

/** A point of a route: the middle of a square, in squares from the board's top left as shown. */
export interface RoutePoint {
  readonly x: number;
  readonly y: number;
}

export type GhostKind = 'preview' | 'epitaph';

export interface RouteScene {
  /** The latest turn's route, under the pieces, in the color of the side that made it. */
  readonly trail: { readonly points: readonly RoutePoint[]; readonly side: 'S' | 'N' } | null;
  /** A move being previewed or an Epitaph's replay; `occupied` marks the points with a pawn. */
  readonly ghost: {
    readonly points: readonly RoutePoint[];
    readonly occupied: readonly boolean[];
    readonly kind: GhostKind;
  } | null;
  /** Motion reduced: still pictures, each route drawn whole at once. */
  readonly still: boolean;
}

/** The most points a route is drawn with: a longer turn shows its latest part. */
export const MAX_POINTS = 32;
const PAIRS = MAX_POINTS / 2;
/** A point with a pawn on it is told to the shader by this shift across. */
const OCCUPIED_SHIFT = 10;

const VERTEX = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

/**
 * The nearest point of a route to q: R = (distance, arc length there, distance across with its
 * side, the route's length); N = (distance to the nearest point of the route's own, its index,
 * 1 when a pawn stands on it, arc length there); P = where that point is. Past either end of the
 * route, the route's frame carries on (arc length below 0 or beyond the length). The arc length is
 * the segments' own, weighed by how near each is, so that it flows on round a joint instead of
 * jumping where one segment becomes nearer than the other.
 */
const nearest = (name: string) => `
void near_${name}(vec2 q, out vec4 R, out vec4 N, out vec2 P) {
  float d = 1e4;
  float s = 0.0;
  float side = 0.0;
  float run = 0.0;
  float sumW = 0.0;
  float sumS = 0.0;
  float nd = 1e4;
  float ni = 0.0;
  float nocc = 0.0;
  float ns = 0.0;
  vec2 np = vec2(0.0);
  vec2 prev = vec2(0.0);
  for (int k = 0; k < ${MAX_POINTS}; k++) {
    if (k >= u_${name}N) break;
    vec4 pair = u_${name}[k / 2];
    vec2 p = k - (k / 2) * 2 == 0 ? pair.xy : pair.zw;
    float occ = step(8.0, p.x);
    p.x -= ${OCCUPIED_SHIFT}.0 * occ;
    if (k > 0) {
      vec2 ab = p - prev;
      float len = max(length(ab), 1e-4);
      vec2 dir = ab / len;
      vec2 w = q - prev;
      float along = dot(w, dir);
      float h = clamp(along, 0.0, len);
      vec2 r = w - dir * h;
      float dd = length(r);
      bool cap = (k == 1 && along < 0.0) || (k == u_${name}N - 1 && along > len);
      float at = run + (cap ? along : h);
      if (dd < d) {
        float across = dir.x * r.y - dir.y * r.x;
        d = dd;
        s = at;
        side = cap ? across : sign(across) * dd;
      }
#ifdef GL_FRAGMENT_PRECISION_HIGH
      float wgt = 1.0 / pow(dd + 0.004, 4.0);
      sumW += wgt;
      sumS += wgt * at;
#endif
      run += len;
    }
    float pd = length(q - p);
    if (pd < nd) {
      nd = pd;
      ni = float(k);
      nocc = occ;
      ns = run;
      np = p;
    }
    prev = p;
  }
#ifdef GL_FRAGMENT_PRECISION_HIGH
  if (sumW > 0.0) s = sumS / sumW;
#endif
  R = vec4(d, s, side, run);
  N = vec4(nd, ni, nocc, ns);
  P = np;
}
`;

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2 u_res;
uniform float u_time;
uniform float u_still;
uniform float u_light;

uniform vec4 u_trail[${PAIRS}];
uniform int u_trailN;
uniform float u_trailReveal;
uniform vec3 u_trailColor;
uniform float u_trailDim;

uniform vec4 u_ghost[${PAIRS}];
uniform int u_ghostN;
uniform float u_ghostReveal;
uniform vec3 u_ghostColor;
uniform float u_epitaph;

const float TAU = 6.2831853;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * noise(p);
    p = p * 2.07 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v / 0.875;
}

vec4 paint(vec3 c, float a) {
  a = clamp(a, 0.0, 1.0);
  return vec4(c * a, a);
}

vec4 over(vec4 top, vec4 bottom) {
  return top + bottom * (1.0 - top.a);
}

// Inside a line of half-width w at distance d, smoothed over a pixel.
float line(float d, float w, float px) {
  return 1.0 - smoothstep(w - px * 0.6, w + px * 0.6, d);
}

${nearest('trail')}
${nearest('ghost')}

vec4 trail(vec2 q, float px) {
  vec4 R;
  vec4 N;
  vec2 P;
  near_trail(q, R, N, P);
  if (R.x > 0.5) return vec4(0.0);
  float d = R.x;
  float s = R.y;
  float total = max(R.w, 1e-3);
  vec3 c = u_trailColor;
  float shown = 1.0 - smoothstep(u_trailReveal - 0.08, u_trailReveal, s);
  // Fainter where the turn began, full where it ended.
  float age = mix(0.38, 1.0, smoothstep(0.0, total, s));
  float grain = 0.8 + 0.2 * noise(vec2(s * 9.0, R.z * 46.0));
  float band = line(d, 0.034, px * 1.4);
  float glow = exp(-d * d * 70.0);
  float isStart = 1.0 - step(0.5, N.y);
  float nodeShown = step(N.w, u_trailReveal + 0.02);
  float dotIn = line(N.x, 0.07, px) * (1.0 - isStart);
  float rim = line(N.x, 0.094, px) * (1.0 - isStart);
  float ring = line(abs(N.x - 0.11), 0.017, px) * isStart;
  vec3 ink = mix(vec3(0.067, 0.051, 0.141), vec3(1.0), u_light);
  vec4 col = paint(c, glow * mix(0.36, 0.16, u_light) * age * shown);
  col = over(paint(c, band * 0.92 * age * grain * shown), col);
  col = over(paint(ink, (rim - dotIn) * 0.95 * nodeShown), col);
  col = over(paint(c, (dotIn + ring * 0.8 * age) * nodeShown), col);
  return col * u_trailDim;
}

vec4 ghost(vec2 q, float px) {
  vec4 R;
  vec4 N;
  vec2 P;
  near_ghost(q, R, N, P);
  if (R.x > 0.75) return vec4(0.0);
  float d = R.x;
  float s = R.y;
  float side = R.z;
  float total = max(R.w, 1e-3);
  float t = u_time;
  vec3 c = u_ghostColor;
  float reveal = u_ghostReveal;
  float epitaph = u_epitaph;

  // The nearest of the route's own points: the start (the pawn that moves), a landing, the last
  // landing; a landing with a pawn on it is ringed around that pawn.
  float isStart = 1.0 - step(0.5, N.y);
  float isEnd = step(float(u_ghostN) - 1.5, N.y);
  float occ = N.z * (1.0 - isStart);
  float nodeShown = smoothstep(N.w - 0.02, N.w + 0.08, reveal);

  // The route: from the edge of the pawn that moves, as far as it is drawn, short of a pawn.
  float m = (1.0 - smoothstep(reveal - 0.1, reveal, s)) * smoothstep(0.14, 0.4, s);
  m *= 1.0 - occ * (1.0 - smoothstep(0.3, 0.42, N.x));
  // The tip of a route being drawn in.
  float tipS = s - reveal;
  float tip = exp(-(tipS * tipS + d * d) * 260.0) * step(reveal, total - 0.01);

  float core = line(d, mix(0.024, 0.017, epitaph), px);
  float glow = exp(-d * d * 55.0);
  float halo = exp(-d * d * 11.0);

  // Arrows of light running along the route, toward its end (not past it).
  float f = fract((s + d * 1.5) * 2.2 - t * mix(1.5, 0.7, epitaph));
  float arrow = smoothstep(0.58, 0.93, f) * (1.0 - smoothstep(0.93, 0.995, f));
  arrow *= (1.0 - smoothstep(0.05, 0.1, d)) * (1.0 - smoothstep(total - 0.2, total - 0.05, s));

  // The nodes.
  float r = mix(mix(0.095, 0.15, isEnd), 0.41, occ);
  float ringW = mix(0.017, 0.022, isEnd);
  vec2 v = q - P;
  float turn = fract(atan(v.y, v.x + 1e-6) / TAU * 14.0 + t * 0.15 * (1.0 - u_still));
  float dash = smoothstep(0.08, 0.16, turn) * (1.0 - smoothstep(0.62, 0.7, turn));
  float ring = line(abs(N.x - r), ringW, px) * (1.0 - isStart) * mix(1.0, dash, occ);
  float ringGlow = exp(-(N.x - r) * (N.x - r) * 420.0) * (1.0 - isStart);
  float dotIn = line(N.x, mix(0.036, 0.052, isEnd), px) * (1.0 - isStart) * (1.0 - occ);
  float k = fract(t * 0.55);
  float pulse = line(abs(N.x - r - mix(0.3, 0.12, occ) * k), 0.012, px);
  pulse *= (1.0 - k) * (1.0 - k) * isEnd * (1.0 - u_still);

  vec3 hot = mix(c, vec3(1.0), 0.72);
  // On a dark board, light. On a light one, a ribbon of ink with white arrows in it, and a white
  // rim to the rings.
  vec3 coreC = mix(hot, c, u_light);
  vec3 rimC = mix(c, vec3(1.0), u_light);
  vec4 col;
  if (epitaph < 0.5) {
    float plasma = fbm(vec2(s * 3.2 - t * 2.0, d * 9.0));
    float ribbon = line(d, mix(0.024, 0.05, u_light), px);
    float inRibbon = mix(1.0 - smoothstep(0.05, 0.1, d), line(d, 0.038, px), u_light);
    col = paint(c, halo * mix(0.2, 0.12, u_light) * m);
    col = over(paint(c, glow * mix(0.25 + 0.55 * plasma, 0.16, u_light) * m), col);
    col = over(paint(coreC, ribbon * m), col);
    col = over(paint(vec3(1.0), arrow * inRibbon * 0.9 * m), col);
  } else {
    // Smoke rising off the route, and drifting along it; motes drifting along it.
    float smoke = fbm(q * 2.4 + vec2(0.0, t * 0.4)) * 0.6
      + fbm(vec2(s * 4.2 - t * 1.0 + 3.7, d * 10.0)) * 0.4;
    float wisp = smoothstep(0.4, 0.8, smoke) * exp(-d * d * 16.0);
    float u = s * 2.6 - t * 0.7;
    float cell = floor(u);
    float r1 = hash(vec2(cell, 3.1));
    float r2 = hash(vec2(cell, 7.7));
    vec2 mp = vec2((fract(u) - 0.2 - 0.6 * r1) / 2.6, side - (r2 - 0.5) * 0.26);
    float mote = exp(-dot(mp, mp) * 2600.0) * (0.55 + 0.45 * sin(t * 3.0 + r1 * TAU));
    float flicker = 0.78 + 0.22 * sin(t * 4.0 + s * 3.0);
    col = paint(c, halo * mix(0.12, 0.1, u_light) * m);
    col = over(paint(c, (glow * 0.35 + wisp * mix(0.6, 0.45, u_light)) * m), col);
    col = over(paint(coreC, core * flicker * m), col);
    col = over(paint(mix(vec3(1.0), c, u_light), arrow * 0.45 * m), col);
    col = over(paint(mix(vec3(1.0), c, u_light), mote * 0.95 * m), col);
  }
  col = over(paint(rimC, ringGlow * mix(0.55, 0.85, u_light) * nodeShown), col);
  col = over(paint(coreC, (ring + dotIn) * nodeShown), col);
  col = over(paint(c, pulse * 0.8 * nodeShown), col);
  col = over(paint(mix(vec3(1.0), c, u_light), tip * 0.9), col);
  return col;
}

void main() {
  vec2 q = vec2(gl_FragCoord.x, u_res.y - gl_FragCoord.y) / u_res * 6.0;
  float px = 6.0 / u_res.x;
  gl_FragColor = over(ghost(q, px), trail(q, px));
}
`;

type Rgb = readonly [number, number, number];

interface Palette {
  readonly S: Rgb;
  readonly N: Rgb;
  readonly preview: Rgb;
  readonly epitaph: Rgb;
  /** The board is light (Sea): ink on white instead of light on the dark. */
  readonly light: boolean;
}

/** A CSS color as 0-1 channels: #rgb, #rrggbb, or rgb()/rgba(); null for anything else. */
export function parseColor(css: string): Rgb | null {
  const s = css.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(s)?.[1];
  const channels = hex
    ? (hex.length === 3
        ? [...hex].map((h) => h + h)
        : [0, 2, 4].map((i) => hex.slice(i, i + 2))
      ).map((h) => parseInt(h, 16))
    : /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(s)?.slice(1, 4).map(Number);
  if (!channels) return null;
  const [r, g, b] = channels.map((v) => Math.min(1, v / 255)) as [number, number, number];
  return [r, g, b];
}

function readPalette(el: HTMLElement): Palette {
  const style = getComputedStyle(el);
  const color = (name: string, fallback: Rgb) =>
    parseColor(style.getPropertyValue(name)) ?? fallback;
  return {
    S: color('--route-S', [0.42, 0.71, 1]),
    N: color('--route-N', [0.92, 0.31, 0.6]),
    preview: color('--route-preview', [0.5, 0.88, 1]),
    epitaph: color('--route-epitaph', [0.49, 0.95, 0.77]),
    light: style.getPropertyValue('--route-on-light').trim() === '1',
  };
}

/** The part of a route that is drawn: its latest MAX_POINTS points. */
export function drawnPart<T>(points: readonly T[]): readonly T[] {
  return points.length > MAX_POINTS ? points.slice(points.length - MAX_POINTS) : points;
}

/** A route's points, two to a vec4 as the shader reads them; a point with a pawn is shifted. */
export function packRoute(
  points: readonly RoutePoint[],
  occupied: readonly boolean[] = [],
): { data: Float32Array; count: number } {
  const from = Math.max(0, points.length - MAX_POINTS);
  const data = new Float32Array(MAX_POINTS * 2);
  for (let i = from; i < points.length; i++) {
    const p = points[i]!;
    data[(i - from) * 2] = p.x + (occupied[i] ? OCCUPIED_SHIFT : 0);
    data[(i - from) * 2 + 1] = p.y;
  }
  return { data, count: points.length - from };
}

export function routeLength(points: readonly RoutePoint[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++)
    total += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
  return total;
}

/**
 * How much of a new route was drawn already: the length of the start it shares with the route
 * shown before it (all of it when the turn only grew, or lost its last move; none for another).
 */
export function sharedLength(
  before: readonly RoutePoint[] | null | undefined,
  after: readonly RoutePoint[],
): number {
  if (!before) return 0;
  const a = drawnPart(before);
  const b = drawnPart(after);
  let k = 0;
  while (k < a.length && k < b.length && a[k]!.x === b[k]!.x && a[k]!.y === b[k]!.y) k++;
  return k < 2 ? 0 : routeLength(b.slice(0, k));
}

/** How long a route takes to be drawn in, in ms, by the length still to draw (in squares). */
export function drawInMs(length: number): number {
  return length <= 0 ? 0 : Math.min(600, 180 + 70 * length);
}

interface DrawIn {
  readonly from: number;
  readonly to: number;
  readonly start: number;
  readonly ms: number;
}

/** How far a route is drawn at a time; past its end once it is drawn whole. */
function drawnTo(a: DrawIn, now: number): number {
  const k = a.ms <= 0 ? 1 : Math.min(1, Math.max(0, (now - a.start) / a.ms));
  if (k >= 1) return a.to + 1;
  const eased = 1 - (1 - k) * (1 - k) * (1 - k);
  return a.from + (a.to - a.from) * eased;
}

const FRAME_MS = 1000 / 30;
/** The time of a still picture: arrows and smoke in a pleasant place. */
const STILL_TIME = 0.35;

const UNIFORMS = [
  'u_res',
  'u_time',
  'u_still',
  'u_light',
  'u_trail',
  'u_trailN',
  'u_trailReveal',
  'u_trailColor',
  'u_trailDim',
  'u_ghost',
  'u_ghostN',
  'u_ghostReveal',
  'u_ghostColor',
  'u_epitaph',
] as const;
type Uniform = (typeof UNIFORMS)[number];

interface Gl {
  readonly gl: WebGLRenderingContext;
  readonly program: WebGLProgram;
  readonly shaders: readonly WebGLShader[];
  readonly buffer: WebGLBuffer | null;
  readonly u: Record<Uniform, WebGLUniformLocation | null>;
  readonly software: boolean;
}

/**
 * Draws the routes of one board on its canvas. The WebGL context is made when a route is first
 * shown; `onFail` is called when it cannot be (or is lost later), and the board then draws its
 * routes with SVG.
 */
export class RoutePainter {
  private g: Gl | null = null;
  private failed = false;
  private scene: RouteScene = { trail: null, ghost: null, still: true };
  private trailIn: DrawIn = { from: 0, to: 0, start: 0, ms: 0 };
  private ghostIn: DrawIn = { from: 0, to: 0, start: 0, ms: 0 };
  private palette: Palette | null = null;
  private frame = 0;
  private last = 0;
  private struggling = false;
  private readonly watch = new FrameWatch();
  private readonly began = performance.now();
  private readonly resizes: ResizeObserver | null;
  private readonly themes: MutationObserver | null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly onFail: () => void,
  ) {
    this.resizes =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.kick());
    this.resizes?.observe(canvas);
    this.themes =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            this.palette = null;
            this.kick();
          });
    this.themes?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    document.addEventListener('visibilitychange', this.onVisibility);
    canvas.addEventListener('webglcontextlost', this.onLost);
  }

  /** Shows these routes: a new or changed one is drawn in (at once when still). */
  update(scene: RouteScene): void {
    if (this.failed) return;
    const now = performance.now();
    const before = this.scene;
    if (!samePoints(before.trail?.points, scene.trail?.points) && scene.trail)
      this.trailIn = drawIn(before.trail?.points, scene.trail.points, now, scene.still);
    if (!samePoints(before.ghost?.points, scene.ghost?.points) && scene.ghost)
      this.ghostIn = drawIn(before.ghost?.points, scene.ghost.points, now, scene.still);
    this.scene = scene;
    if (!this.g && (scene.trail || scene.ghost) && !this.init()) {
      this.fail();
      return;
    }
    this.kick();
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.resizes?.disconnect();
    this.themes?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.canvas.removeEventListener('webglcontextlost', this.onLost);
    const gl = this.release();
    // The context goes back once the canvas has left the page: an effect run again on the same
    // canvas (React does so in development) takes it up again instead.
    const canvas = this.canvas;
    if (gl) setTimeout(() => !canvas.isConnected && loseContext(gl), 0);
  }

  private init(): boolean {
    let gl: WebGLRenderingContext | null = null;
    try {
      gl = this.canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        powerPreference: 'low-power',
      });
    } catch {
      gl = null;
    }
    if (!gl || gl.isContextLost()) return false;
    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (vs && fs && program) {
      gl.attachShader(program, vs);
      gl.attachShader(program, fs);
      gl.linkProgram(program);
    }
    if (!vs || !fs || !program || !gl.getProgramParameter(program, gl.LINK_STATUS)) {
      if (program) console.warn('Route shader:', gl.getProgramInfoLog(program));
      loseContext(gl);
      return false;
    }
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    // One triangle that covers the canvas.
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    const u = Object.fromEntries(
      UNIFORMS.map((name) => [name, gl.getUniformLocation(program, name)]),
    ) as Record<Uniform, WebGLUniformLocation | null>;
    gl.clearColor(0, 0, 0, 0);
    this.g = { gl, program, shaders: [vs, fs], buffer, u, software: softwareRendered(gl) };
    return true;
  }

  /** Deletes what the painter made in its context, and returns the context. */
  private release(): WebGLRenderingContext | null {
    const g = this.g;
    this.g = null;
    if (!g) return null;
    const { gl } = g;
    gl.deleteBuffer(g.buffer);
    gl.deleteProgram(g.program);
    for (const s of g.shaders) gl.deleteShader(s);
    return gl;
  }

  private fail(): void {
    this.failed = true;
    cancelAnimationFrame(this.frame);
    const gl = this.release();
    if (gl) loseContext(gl);
    this.onFail();
  }

  private readonly onLost = (e: Event) => {
    e.preventDefault();
    this.g = null;
    this.fail();
  };

  private readonly onVisibility = () => {
    this.watch.reset();
    this.kick();
  };

  private still(): boolean {
    return this.scene.still || this.struggling || (this.g?.software ?? true);
  }

  /** Something moves: a move or an Epitaph shown, or a route being drawn in. */
  private moving(now: number): boolean {
    if (this.still() || document.hidden) return false;
    return (
      this.scene.ghost !== null ||
      (this.scene.trail !== null && drawnTo(this.trailIn, now) <= this.trailIn.to)
    );
  }

  /** Draws now, and goes on drawing while something moves. */
  private kick(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    if (!this.g) return;
    const now = performance.now();
    this.draw(now);
    if (this.moving(now)) {
      this.last = now;
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  private readonly tick = (now: number) => {
    this.frame = requestAnimationFrame(this.tick);
    if (this.watch.struggling(now)) this.struggling = true;
    if (now - this.last < FRAME_MS && !this.struggling) return;
    this.last = now;
    this.draw(now);
    if (!this.moving(now)) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  };

  private draw(now: number): void {
    const g = this.g;
    if (!g) return;
    const { gl, u } = g;
    const canvas = this.canvas;
    const ratio = g.software ? 1 : Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(canvas.clientWidth * ratio));
    const h = Math.max(1, Math.round(canvas.clientHeight * ratio));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const { trail, ghost } = this.scene;
    if (!trail && !ghost) return;
    const palette = (this.palette ??= readPalette(canvas));
    const still = this.still();
    gl.uniform2f(u.u_res, w, h);
    gl.uniform1f(u.u_time, still ? STILL_TIME : ((now - this.began) / 1000) % 1000);
    gl.uniform1f(u.u_still, still ? 1 : 0);
    gl.uniform1f(u.u_light, palette.light ? 1 : 0);
    const t = packRoute(trail?.points ?? []);
    gl.uniform4fv(u.u_trail, t.data);
    gl.uniform1i(u.u_trailN, trail ? t.count : 0);
    gl.uniform1f(u.u_trailReveal, still ? this.trailIn.to + 1 : drawnTo(this.trailIn, now));
    gl.uniform3fv(u.u_trailColor, [...(trail?.side === 'N' ? palette.N : palette.S)]);
    // A move or an Epitaph shown: the trail steps back.
    gl.uniform1f(u.u_trailDim, ghost ? 0.55 : 1);
    const p = packRoute(ghost?.points ?? [], ghost?.occupied);
    gl.uniform4fv(u.u_ghost, p.data);
    gl.uniform1i(u.u_ghostN, ghost ? p.count : 0);
    gl.uniform1f(u.u_ghostReveal, still ? this.ghostIn.to + 1 : drawnTo(this.ghostIn, now));
    gl.uniform3fv(u.u_ghostColor, [
      ...(ghost?.kind === 'epitaph' ? palette.epitaph : palette.preview),
    ]);
    gl.uniform1f(u.u_epitaph, ghost?.kind === 'epitaph' ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
}

/** Hands a context back at once: browsers keep only a few. */
function loseContext(gl: WebGLRenderingContext): void {
  if (!gl.isContextLost()) gl.getExtension('WEBGL_lose_context')?.loseContext();
}

function samePoints(
  a: readonly RoutePoint[] | null | undefined,
  b: readonly RoutePoint[] | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  return a.length === b.length && a.every((p, i) => p.x === b[i]!.x && p.y === b[i]!.y);
}

function drawIn(
  before: readonly RoutePoint[] | null | undefined,
  after: readonly RoutePoint[],
  now: number,
  still: boolean,
): DrawIn {
  const to = routeLength(drawnPart(after));
  const from = still ? to : Math.min(to, sharedLength(before, after));
  return { from, to, start: now, ms: drawInMs(to - from) };
}
